import { useMemo } from "react";
import { OrganizationType } from "@remi/enums/organization-type";
import { RnBuilding, RnHome } from "@remi/icons/next";
import { cn } from "@remi/next-ui/utils";

import { TruncatedTags } from "~/components/ui/TruncatedTags";
import { useOrganization } from "~/hooks/useOrganization";
import { usePerson } from "~/hooks/usePerson";
import { PersonTagIcons } from "~/utils/records/person-tag-icons";

export const OrganizationBadge = ({
  personId,
  className,
  multiline = false,
}: {
  personId: string;
  className?: string;
  multiline?: boolean;
}) => {
  const { data: person } = usePerson(personId);
  const { data: organization } = useOrganization(person?.organizationId);

  const icon = useMemo(() => {
    if (!organization || !person) {
      return null;
    }

    if (organization.type === OrganizationType.HOMEOWNER) {
      if (person.tags[0]) {
        return PersonTagIcons[person.tags[0].tag] ?? <RnHome />;
      }
      return <RnHome />;
    }
    return <RnBuilding />;
  }, [organization, person]);

  const label = useMemo(() => {
    if (!organization || !person) {
      return "";
    }
    if (organization.type === OrganizationType.REMI_INTERNAL) {
      return "Remi";
    }
    if (organization.type === OrganizationType.HOMEOWNER) {
      if (person.tags.length > 0) {
        return (
          <TruncatedTags
            tags={person.tags}
            maxLength={30}
            separator=", "
            className="font-medium text-text-secondary text-xs"
          />
        );
      }
      return "Homeowner";
    }
    return organization.name;
  }, [organization, person]);

  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-0.5 rounded-full pr-2 pl-1.5",
        multiline ? "w-fit items-start gap-1 rounded-[10px] py-0.5" : "h-5",
        className,
      )}
    >
      <span className={cn("shrink-0 text-icon-secondary", multiline && "mt-px")}>{icon}</span>
      {typeof label === "string" ? (
        <span className={cn("min-w-0 font-medium text-text-secondary text-xs", multiline ? "break-words" : "truncate")}>
          {label}
        </span>
      ) : (
        label
      )}
    </div>
  );
};
